/**
 * File: pipelineViewUtils.ts
 * Path: apps/admin/src/shared/utils/pipelineViewUtils.ts
 * Description: Date formatting and the remembered Board / List choice for the
 * pipeline page.
 */

export type PipelineView = "board" | "list";

const VIEW_KEY = "idk.leads.pipelineView";

/**
 * The view this browser used last. With nothing stored, phones start on the
 * list (a six-column board is mostly sideways scrolling there) and wider
 * screens on the board. Storage can throw (private mode, blocked site data),
 * so every access is guarded and falls back to the default.
 */
export const loadPipelineView = (): PipelineView => {
  try {
    const stored = window.localStorage.getItem(VIEW_KEY);
    if (stored === "board" || stored === "list") return stored;
  } catch {
    // Fall through to the screen-size default.
  }
  if (typeof window.matchMedia === "function" && window.matchMedia("(max-width: 639px)").matches) {
    return "list";
  }
  return "board";
};

export const savePipelineView = (view: PipelineView): void => {
  try {
    window.localStorage.setItem(VIEW_KEY, view);
  } catch {
    // Not remembered this time; the view still changes.
  }
};

const DAY = new Intl.DateTimeFormat("en-ZA", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

const STAMP = new Intl.DateTimeFormat("en-ZA", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "Africa/Johannesburg",
});

/** "2026-10-01" → "1 Oct 2026". Read as UTC so the day never shifts. */
export const formatDay = (isoDate: string): string =>
  DAY.format(new Date(`${isoDate}T00:00:00Z`));

/** Epoch millis → the South African calendar day it fell on. */
export const formatStamp = (ms: number | null): string => (ms === null ? "" : STAMP.format(ms));
